import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://rentasquad.co';
const SITE_NAME = 'Rent a Squad';
const DEFAULT_DESCRIPTION =
  'Squad-as-a-Service entregue pela Arcano Projetos. Recrute com quem já foi desenvolvedor e com a metodologia Torke®.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/logo-azul.png`;

/**
 * Generic SEO component.
 *
 * Props:
 *  - title: page title (will be suffixed with " | Rent a Squad")
 *  - description: meta description
 *  - path: page path (default current pathname) — used to build canonical URL
 *  - image: og/twitter image URL (absolute)
 *  - noIndex: when true, sets robots noindex
 *  - jsonLd: object/array to embed as JSON-LD structured data
 */
export const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_OG_IMAGE,
  noIndex = false,
  jsonLd = null,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Squad as a Service by Arcano Projetos`;
  const pathname =
    path ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const canonical = `${SITE_URL}${pathname}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonical} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* JSON-LD (accepts a single object or an array of schemas) */}
      {jsonLd && (
        Array.isArray(jsonLd)
          ? jsonLd.map((schema, i) => (
              <script key={schema?.['@type'] || i} type="application/ld+json">
                {JSON.stringify(schema)}
              </script>
            ))
          : (
            <script type="application/ld+json">
              {JSON.stringify(jsonLd)}
            </script>
          )
      )}
    </Helmet>
  );
};

export default SEO;
